// Google Photorealistic 3D Tiles world. The OSM city stays in the scene as the invisible
// hit-test/destruction layer; this module streams the photo mesh, lines it up with the OSM
// frame (x = east, z = south, y = up, metres, origin at the city centre, ground ≈ y 0), cuts
// destroyed footprints out of it with a void mask, and exposes raycast + attribution.
import * as THREE from 'three';
import { TilesRenderer } from '3d-tiles-renderer';
import { GoogleCloudAuthPlugin, ReorientationPlugin, TileCompressionPlugin, GLTFExtensionsPlugin } from '3d-tiles-renderer/plugins';
import { DRACOLoader } from 'three/addons/loaders/DRACOLoader.js';
import { MeshBVH, acceleratedRaycast } from 'three-mesh-bvh';
import { scene, camera, renderer } from '../game/scene';
import { BB } from '../game/state';

export const GOOGLE_KEY: string = (import.meta.env.VITE_GOOGLE_MAPS_KEY || '').trim();
export const photoAvailable = GOOGLE_KEY.length > 0;

const MASK_PX = 1024;                 // void mask resolution
const MASK_R = BB.half + 200;         // metres covered from the origin in each direction
const PIT = 1.5;                      // depth of the dark floor revealed under a cut building
const ROOT_TIMEOUT = 15000;           // ms to wait for the root tileset before falling back

type Status = 'off' | 'loading' | 'ready' | 'failed';
export const T = {
  status: 'off' as Status, tiles: null as TilesRenderer | null, root: null as THREE.Group | null,
  cityKey: '', lat: 0, lon: 0, groundY: 0, groundSamples: 0, groundStable: false, probes: [[0, 0]] as number[][], lastMedian: 0,
  loaded: 0, errors: 0, attribution: '', lastAttrib: 0, visibleTiles: 0, failReason: '',
};

// ── void mask: R = cut flag, G/B = 16-bit ground height at that footprint (0.015 m steps over −256…768 m)
const maskC = document.createElement('canvas'); maskC.width = maskC.height = MASK_PX;
const maskG = maskC.getContext('2d', { willReadFrequently: false })!;
const maskTex = new THREE.CanvasTexture(maskC);
maskTex.flipY = false; maskTex.colorSpace = THREE.NoColorSpace; maskTex.minFilter = maskTex.magFilter = THREE.NearestFilter; maskTex.generateMipmaps = false;
export const voidUniforms = {
  uVoidMask: { value: maskTex },
  uVoidRect: { value: new THREE.Vector4(-MASK_R, -MASK_R, 1 / (2 * MASK_R), 1 / (2 * MASK_R)) },
  uVoidOn: { value: 0 },
  uHeatPos: { value: new THREE.Vector3(0, -1e4, 0) }, uHeat: { value: 0 },
  uFires: { value: new Float32Array(6 * 4) },
  uCollA: { value: new Float32Array(8 * 4) },   // cx, cz, hx, hz
  uCollB: { value: new Float32Array(8 * 4) },   // cutY, gy, 0, 0
  uCollN: { value: 0 },
};
const collapses: Array<{ cx: number; cz: number; hx: number; hz: number; top: number; gy: number; t: number }> = [];
export const COLLAPSE_S = 0.75;
// a destroyed footprint slices down from its roof to street level over COLLAPSE_S seconds (photo world)
export function startCollapse(cx: number, cz: number, hx: number, hz: number, top: number, gy: number){ if (collapses.length >= 8) collapses.shift(); collapses.push({ cx, cz, hx: hx + 2.5, hz: hz + 2.5, top, gy, t: 0 }); }
export function updateCollapses(dt: number){
  const A = voidUniforms.uCollA.value, B = voidUniforms.uCollB.value; let n = 0;
  for (let i = collapses.length - 1; i >= 0; i--){ const c = collapses[i]; c.t += dt; if (c.t >= COLLAPSE_S){ collapses.splice(i, 1); continue; } }
  for (const c of collapses){ const k = c.t / COLLAPSE_S; const e = k * k * (3 - 2 * k); A[n*4] = c.cx; A[n*4+1] = c.cz; A[n*4+2] = c.hx; A[n*4+3] = c.hz; B[n*4] = c.gy + 0.3 + (c.top - c.gy) * (1 - e); B[n*4+1] = c.gy; n++; }
  voidUniforms.uCollN.value = n;
}
let voidCount = 0;
export function clearVoids(){ maskG.clearRect(0, 0, MASK_PX, MASK_PX); maskTex.needsUpdate = true; voidCount = 0; voidUniforms.uVoidOn.value = 0; }
const toPx = (x: number, z: number) => [(x + MASK_R) / (2 * MASK_R) * MASK_PX, (z + MASK_R) / (2 * MASK_R) * MASK_PX];
export function paintVoid(pts: number[][], groundY: number){
  if (pts.length < 3) return;
  const h = Math.round(THREE.MathUtils.clamp((groundY + 256) / 1024, 0, 1) * 65535);
  maskG.fillStyle = maskG.strokeStyle = `rgb(255,${h >> 8},${h & 255})`;
  maskG.lineWidth = 2.2 * MASK_PX / (2 * MASK_R);   // ≈2.2 m dilation so photo-mesh walls that overhang the OSM footprint still go
  maskG.lineJoin = 'round';
  maskG.beginPath();
  pts.forEach((p, i) => { const [x, y] = toPx(p[0], p[1]); i ? maskG.lineTo(x, y) : maskG.moveTo(x, y); });
  maskG.closePath(); maskG.fill(); maskG.stroke();
  maskTex.needsUpdate = true; voidCount++; voidUniforms.uVoidOn.value = 1;
}
export function paintDisc(x: number, z: number, r: number, groundY: number){
  const h = Math.round(THREE.MathUtils.clamp((groundY + 256) / 1024, 0, 1) * 65535);
  maskG.fillStyle = `rgb(255,${h >> 8},${h & 255})`;
  const [px, pz] = toPx(x, z); maskG.beginPath(); maskG.arc(px, pz, r * MASK_PX / (2 * MASK_R), 0, Math.PI * 2); maskG.fill();
  maskTex.needsUpdate = true; voidCount++; voidUniforms.uVoidOn.value = 1;
}

// ── material patch: unlit photo texture + void discard + beam heat + fire glow
function patchMaterial(mat: THREE.Material){
  mat.onBeforeCompile = sh => {
    Object.assign(sh.uniforms, voidUniforms);
    sh.vertexShader = sh.vertexShader
      .replace('#include <common>', '#include <common>\nvarying vec3 vVoidPos;\nuniform sampler2D uVoidMask; uniform vec4 uVoidRect; uniform float uVoidOn; uniform vec4 uCollA[8]; uniform vec4 uCollB[8]; uniform int uCollN;\nfloat polarizeCut(vec3 wp, float gy){ float cut = gy + 0.3; for (int i = 0; i < 8; i++){ if (i >= uCollN) break; vec4 a = uCollA[i]; if (abs(wp.x - a.x) <= a.z && abs(wp.z - a.y) <= a.w) cut = max(cut, uCollB[i].x); } return cut; }')
      .replace('#include <begin_vertex>', `#include <begin_vertex>
        { vec4 wp = modelMatrix * vec4(transformed, 1.0);
          if (uVoidOn > 0.5) { vec2 vuv = (wp.xz - uVoidRect.xy) * uVoidRect.zw;
            if (all(greaterThan(vuv, vec2(0.0))) && all(lessThan(vuv, vec2(1.0)))) { vec3 m = texture2D(uVoidMask, vuv).rgb;
              float gy = (m.g * 255.0 * 256.0 + m.b * 255.0) / 65535.0 * 1024.0 - 256.0;
              // a destroyed building's vertices drop to street level (sliding down during a collapse): the roof/wall texture becomes its foundation slab
              if (m.r > 0.5) { float cut = polarizeCut(wp.xyz, gy); if (wp.y > cut) { wp.y = cut; transformed = (inverse(modelMatrix) * wp).xyz; } } } }
          vVoidPos = (modelMatrix * vec4(transformed, 1.0)).xyz; }`);
    sh.fragmentShader = sh.fragmentShader
      .replace('#include <common>', `#include <common>
        varying vec3 vVoidPos;
        uniform sampler2D uVoidMask; uniform vec4 uVoidRect; uniform float uVoidOn;
        uniform vec3 uHeatPos; uniform float uHeat; uniform vec4 uFires[6]; uniform vec4 uCollA[8]; uniform vec4 uCollB[8]; uniform int uCollN;
        float polarizeCut(vec3 wp, float gy){ float cut = gy + 0.3; for (int i = 0; i < 8; i++){ if (i >= uCollN) break; vec4 a = uCollA[i]; if (abs(wp.x - a.x) <= a.z && abs(wp.z - a.y) <= a.w) cut = max(cut, uCollB[i].x); } return cut; }`)
      .replace('#include <clipping_planes_fragment>', `#include <clipping_planes_fragment>
        if (uVoidOn > 0.5) {
          vec2 vuv = (vVoidPos.xz - uVoidRect.xy) * uVoidRect.zw;
          if (all(greaterThan(vuv, vec2(0.0))) && all(lessThan(vuv, vec2(1.0)))) {
            vec3 m = texture2D(uVoidMask, vuv).rgb;
            float gy = (m.g * 255.0 * 256.0 + m.b * 255.0) / 65535.0 * 1024.0 - 256.0;
            if (m.r > 0.5 && vVoidPos.y > polarizeCut(vVoidPos, gy) + 0.05) discard;
          }
        }`)
      .replace('#include <dithering_fragment>', `#include <dithering_fragment>
        { float d = distance(vVoidPos, uHeatPos); float k = uHeat * smoothstep(22.0, 0.0, d);
          gl_FragColor.rgb += vec3(1.0, 0.42, 0.18) * k * 1.0 + vec3(1.0, 0.85, 0.6) * k * k * 1.2;
          for (int i = 0; i < 6; i++) { vec4 f = uFires[i]; if (f.w <= 0.0) continue;
            float fd = distance(vVoidPos, f.xyz); float fk = f.w * smoothstep(60.0, 4.0, fd);
            gl_FragColor.rgb += vec3(1.0, 0.45, 0.12) * fk; } }`);
  };
  mat.customProgramCacheKey = () => 'polarize-tile';
  mat.needsUpdate = true;
}

const downRay = new THREE.Raycaster(); downRay.firstHitOnly = true;
const _down = new THREE.Vector3(0, -1, 0), _from = new THREE.Vector3();

export function startTiles(cityKey: string, lat: number, lon: number, log: (m: string) => void): Promise<void> {
  if (!photoAvailable) return Promise.reject(new Error('no key'));
  if (T.tiles && T.cityKey === cityKey && T.status === 'ready'){ T.root!.visible = true; return Promise.resolve(); }
  stopTiles();
  const tiles = new TilesRenderer();
  tiles.registerPlugin(new GoogleCloudAuthPlugin({ apiToken: GOOGLE_KEY, autoRefreshToken: true }));
  tiles.registerPlugin(new TileCompressionPlugin());
  const draco = new DRACOLoader(); draco.setDecoderPath('/draco/');
  tiles.registerPlugin(new GLTFExtensionsPlugin({ dracoLoader: draco }));
  tiles.registerPlugin(new ReorientationPlugin({ lat: lat * THREE.MathUtils.DEG2RAD, lon: lon * THREE.MathUtils.DEG2RAD }));
  tiles.errorTarget = 14;
  (tiles as any).lruCache.minSize = 800; (tiles as any).lruCache.maxSize = 1400;
  tiles.setCamera(camera); tiles.setResolutionFromRenderer(camera, renderer);
  // ReorientationPlugin yields +X west / +Z north; the game frame is +X east / +Z south → spin 180° about Y
  const root = new THREE.Group(); root.rotation.y = Math.PI; root.add(tiles.group); scene.add(root);
  Object.assign(T, { status: 'loading', tiles, root, cityKey, lat, lon, groundY: 0, groundSamples: 0, groundStable: false, loaded: 0, errors: 0, attribution: '', visibleTiles: 0, failReason: '' });

  tiles.addEventListener('load-model', ({ scene: s }: any) => {
    s.traverse((c: any) => {
      if (!c.isMesh) return;
      c.geometry.boundsTree = new MeshBVH(c.geometry); c.raycast = acceleratedRaycast;
      const old = c.material; const mats = Array.isArray(old) ? old : [old];
      const next = mats.map((m: any) => { const b = new THREE.MeshBasicMaterial({ map: m.map || null, color: m.color ? m.color.clone() : 0xffffff, fog: true }); patchMaterial(b); return b; });
      c.material = Array.isArray(old) ? next : next[0]; c.userData.polarizeMats = next; c.userData.origMats = mats;
      c.castShadow = false; c.receiveShadow = false;
    });
    T.loaded++;
  });
  tiles.addEventListener('dispose-model', ({ scene: s }: any) => {
    s.traverse((c: any) => { if (c.isMesh){ c.geometry.boundsTree = null; for (const m of c.userData.polarizeMats || []) m.dispose(); } });
  });
  tiles.addEventListener('load-error', (e: any) => { T.errors++; if (T.status === 'loading' && (!e.tile || e.tile === tiles.root || !tiles.root)) { T.failReason = String(e.error?.message || e.error || 'load error'); } });
  tiles.addEventListener('tile-visibility-change', () => { T.lastAttrib = 0; });

  return new Promise<void>((resolve, reject) => {
    const t0 = performance.now();
    const fail = (why: string) => { T.status = 'failed'; T.failReason = why; stopTiles(); reject(new Error(why)); };
    const check = () => {
      if (T.status !== 'loading') return;
      if (T.failReason) return fail(T.failReason);
      if (tiles.root && T.loaded >= 4){ T.status = 'ready'; log(`Photo tiles streaming · ${T.loaded} loaded`); return resolve(); }
      if (performance.now() - t0 > ROOT_TIMEOUT) return fail(tiles.root ? 'tiles not arriving' : 'root tileset unreachable');
      log(tiles.root ? `Streaming photo tiles · ${T.loaded} loaded` : 'Requesting photo tileset');
      setTimeout(check, 250);
    };
    check();
  });
}

export function stopTiles(){
  if (T.root){ scene.remove(T.root); }
  if (T.tiles){ try { T.tiles.dispose(); } catch (e) {} }
  T.tiles = null; T.root = null; T.status = 'off'; T.cityKey = ''; T.groundStable = false; T.groundY = 0;
}
export function hideTiles(){ if (T.root) T.root.visible = false; }
export function showTiles(){ if (T.root) T.root.visible = true; }

// Sample the photo terrain height at (x, z) in world metres; null until tiles cover the spot.
export function sampleGround(x: number, z: number): number | null {
  if (!T.tiles || !T.root || !T.root.visible) return null;
  _from.set(x, 4000, z); downRay.set(_from, _down); downRay.far = 8000;
  const hits = downRay.intersectObject(T.tiles.group, true);
  return hits.length ? hits[0].point.y : null;
}

// Per-frame: stream, keep the origin's ground at y ≈ 0, refresh attribution text
export function updateTiles(dt: number, now: number){
  const tiles = T.tiles; if (!tiles || !T.root || !T.root.visible) return;
  updateCollapses(dt);
  tiles.setResolutionFromRenderer(camera, renderer);
  tiles.update();
  T.visibleTiles = tiles.visibleTiles.size;
  if (!T.groundStable && T.status === 'ready' && (now % 500) < dt * 1000 + 1){
    // street level at the origin = median of the photo surface over nearby OSM road points (a single probe can land on a roof)
    const ys: number[] = []; for (const [px, pz] of T.probes){ const y = sampleGround(px, pz); if (y !== null) ys.push(y); }
    if (ys.length){ ys.sort((a, b) => a - b); const y = ys[ys.length >> 1]; T.root.position.y -= y; T.groundSamples++; groundCache.clear(); if (Math.abs(y) < 0.6 && T.groundSamples > 2) T.groundStable = true; }
  }
  if (now - T.lastAttrib > 1000){
    T.lastAttrib = now;
    const list = (tiles as any).getAttributions([]) as Array<{ type: string, value: string }>;
    T.attribution = list.filter(a => a.type === 'string').map(a => a.value).join(' · ');
  }
}

// Beam raycast against the photo mesh (world space). Returns the closest hit or null.
export function raycastTiles(raycaster: THREE.Raycaster): THREE.Intersection | null {
  if (!T.tiles || !T.root || !T.root.visible || T.status !== 'ready') return null;
  const prev = raycaster.firstHitOnly; raycaster.firstHitOnly = true;
  const hits = raycaster.intersectObject(T.tiles.group, true);
  raycaster.firstHitOnly = prev;
  return hits.length ? hits[0] : null;
}
export const PIT_DEPTH = PIT;

// Terrain height under (x, z) for gameplay objects — cached on a 4 m grid, 0 outside the photo world.
const groundCache = new Map<number, number>();
export function groundAt(x: number, z: number, fallback = 0): number {
  if (!T.tiles || !T.root || !T.root.visible || T.status !== 'ready') return 0;
  const k = ((x / 4) | 0) * 65536 + ((z / 4) | 0);
  const c = groundCache.get(k); if (c !== undefined) return c;
  const y = sampleGround(x, z); const v = y === null ? fallback : y;
  if (y !== null){ if (groundCache.size > 20000) groundCache.clear(); groundCache.set(k, v); }
  return v;
}
export function clearGroundCache(){ groundCache.clear(); }

// Street level for walking: the low quartile of a ring of probes around (x, z). Cars, trees, awnings and
// low roofs all sit above the street, so they never lift the giant; real slopes (Nob Hill) still read.
const RING: number[][] = [];
for (let i = 0; i < 8; i++){ const a = i / 8 * Math.PI * 2; RING.push([Math.cos(a) * 15, Math.sin(a) * 15], [Math.cos(a + 0.39) * 32, Math.sin(a + 0.39) * 32]); }
const _ys: number[] = [];
export function streetLevel(x: number, z: number, fallback: number): number {
  if (!T.tiles || !T.root || !T.root.visible || T.status !== 'ready') return fallback;
  _ys.length = 0;
  const c = groundAt(x, z, NaN); if (!Number.isNaN(c)) _ys.push(c);
  for (const [ox, oz] of RING){ const y = groundAt(x + ox, z + oz, NaN); if (!Number.isNaN(y)) _ys.push(y); }
  if (_ys.length < 4) return fallback;
  _ys.sort((a, b) => a - b);
  return _ys[Math.floor(_ys.length * 0.25)];
}

// Average colour of the photo texture around a raycast hit (for rubble tinted like the building it came from).
const pc = document.createElement('canvas'); pc.width = pc.height = 1; const pg = pc.getContext('2d', { willReadFrequently: true });
export function sampleTileColor(hit: THREE.Intersection): THREE.Color | null {
  const m: any = (hit.object as any)?.material; const map = m && !Array.isArray(m) ? m.map : null; const uv = hit.uv;
  if (!pg || !map || !map.image || !uv) return null;
  const img = map.image; const w = img.width, h = img.height; if (!w || !h) return null;
  try {
    const u = ((uv.x % 1) + 1) % 1, v = ((uv.y % 1) + 1) % 1; const sy = map.flipY ? 1 - v : v;
    pg.clearRect(0, 0, 1, 1); pg.drawImage(img, Math.max(0, u * w - 2), Math.max(0, sy * h - 2), 5, 5, 0, 0, 1, 1);
    const d = pg.getImageData(0, 0, 1, 1).data; if (d[3] === 0) return null;
    return new THREE.Color(d[0] / 255, d[1] / 255, d[2] / 255).convertSRGBToLinear();
  } catch (e) { return null; }
}
